import React, { useEffect, VFC } from 'react';
import { Row, Col, Container } from 'react-grid-system';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import Spacing from '../components/atoms/Spacing';
import Typography from '../components/atoms/Typography';
import ProductOrderListItem from '../components/organisms/ProductOrderListItem';
import TableItem from '../components/molecules/TableItem';
import IconButton from '../components/molecules/IconButton';
import ProductImage from '../components/molecules/ProductImage';
import { theme } from '../utils/theme';
import SurroundingBorder from '../components/organisms/SurroundingBorder';
import ListBorder from '../components/organisms/ListBorder';
import { useProductContext } from '../contexts/productContext';
import Loader from '../components/molecules/Loader';
import { useAppNavigate } from '../hooks/useAppNavigate';

const Section = styled.div`
  border-bottom: 1px solid #e0e0e0;
`;

const ProductDetailsPage: VFC = () => {
  const navigate = useAppNavigate();
  const { getProductDetail, productDetail } = useProductContext();
  const params = useParams();

  useEffect(() => {
    getProductDetail({
      productNumber: params.productNumber,
      variant: params.variant,
    });
  }, [getProductDetail, params.productNumber, params.variant]);

  const table = productDetail
    ? [
        {
          key: 'product_name',
          title: (
            <Typography translateKey="product_name" variant="body1-semi-bold" />
          ),
          value: productDetail?.productDescription,
        },
        {
          key: 'product_variant',
          title: (
            <Typography
              translateKey="product_variant"
              variant="body1-semi-bold"
            />
          ),
          value: productDetail?.variantDescription,
        },
      ]
    : [];

  if (!productDetail) {
    return (
      <SurroundingBorder>
        <Loader />
      </SurroundingBorder>
    );
  }

  return (
    <SurroundingBorder>
      <Container>
        <Spacing spacing={4} />
        <Row>
          <Col>
            <IconButton
              icon="arrow-left"
              iconColor={theme.colors.grey[100]}
              backgroundColor={theme.colors.grey[40]}
              onClick={() => navigate(-1)}
            />
          </Col>
        </Row>
        <Spacing spacing={4} />
        <Section>
          <Row justify="between">
            <Spacing horizontal spacing={4} />
            <Col sm="content">
              <Row>
                <ProductImage src={productDetail.imageUrl} />
              </Row>
            </Col>
            <Col>
              <Typography translateKey="art_no" variant="body1-semi-bold" />
              <Typography variant="h2">
                {`${productDetail?.productNumber}${productDetail?.variant}`}
              </Typography>
              <Spacing spacing={2} />
              <Col>
                <Row>
                  {table.map(({ title, value, key }) => (
                    <TableItem key={key} title={title} value={value} />
                  ))}
                </Row>
              </Col>
              <Spacing spacing={4} />
            </Col>
          </Row>
        </Section>
        <ListBorder>
          <Col>
            <Spacing spacing={4} />
            {productDetail.orderLines?.map((item) => (
              <ProductOrderListItem key={item.uid} {...item} />
            ))}
          </Col>
        </ListBorder>
      </Container>
    </SurroundingBorder>
  );
};

export default ProductDetailsPage;
